import { z } from "zod";
import {
  CollaborationInvitationCreateSchema,
  CollaborationInvitationResponseSchema,
  CollaborationMemberUpdateSchema,
  CollaborationResourceUpsertSchema,
  CollaborationResourceVersionQuerySchema,
  CollaborationWorkspaceCreateSchema,
} from "./sync";

const PortalWorkspaceIdSchema = z
  .object({
    workspaceId: z.string().uuid(),
  })
  .strict();

const PortalCollaborationListWorkspacesRequestSchema = z
  .object({
    operation: z.literal("listWorkspaces"),
  })
  .strict();

const PortalCollaborationCreateWorkspaceRequestSchema = z
  .object({
    operation: z.literal("createWorkspace"),
    input: CollaborationWorkspaceCreateSchema,
  })
  .strict();

const PortalCollaborationListInvitationsRequestSchema = z
  .object({
    operation: z.literal("listInvitations"),
  })
  .strict();

const PortalCollaborationRespondInvitationRequestSchema = z
  .object({
    operation: z.literal("respondInvitation"),
    input: z
      .object({
        invitationId: z.string().uuid(),
        response: CollaborationInvitationResponseSchema,
      })
      .strict(),
  })
  .strict();

const PortalCollaborationListMembersRequestSchema = z
  .object({
    operation: z.literal("listMembers"),
    input: PortalWorkspaceIdSchema,
  })
  .strict();

const PortalCollaborationInviteMemberRequestSchema = z
  .object({
    operation: z.literal("inviteMember"),
    input: z
      .object({
        workspaceId: z.string().uuid(),
        invitation: CollaborationInvitationCreateSchema,
      })
      .strict(),
  })
  .strict();

const PortalCollaborationUpdateMemberRequestSchema = z
  .object({
    operation: z.literal("updateMember"),
    input: z
      .object({
        workspaceId: z.string().uuid(),
        userId: z.string().uuid(),
        update: CollaborationMemberUpdateSchema,
      })
      .strict(),
  })
  .strict();

const PortalCollaborationRemoveMemberRequestSchema = z
  .object({
    operation: z.literal("removeMember"),
    input: z
      .object({
        workspaceId: z.string().uuid(),
        userId: z.string().uuid(),
      })
      .strict(),
  })
  .strict();

const PortalCollaborationListResourcesRequestSchema = z
  .object({
    operation: z.literal("listResources"),
    input: PortalWorkspaceIdSchema,
  })
  .strict();

const PortalCollaborationUpsertResourceRequestSchema = z
  .object({
    operation: z.literal("upsertResource"),
    input: z
      .object({
        workspaceId: z.string().uuid(),
        resource: CollaborationResourceUpsertSchema,
      })
      .strict(),
  })
  .strict();

const PortalCollaborationDeleteResourceRequestSchema = z
  .object({
    operation: z.literal("deleteResource"),
    input: z
      .object({
        workspaceId: z.string().uuid(),
        resourceId: z.string().uuid(),
        version: CollaborationResourceVersionQuerySchema.shape.version,
      })
      .strict(),
  })
  .strict();

const PortalCollaborationListAuditRequestSchema = z
  .object({
    operation: z.literal("listAudit"),
    input: PortalWorkspaceIdSchema,
  })
  .strict();

export const PortalCollaborationRequestSchema = z.discriminatedUnion(
  "operation",
  [
    PortalCollaborationListWorkspacesRequestSchema,
    PortalCollaborationCreateWorkspaceRequestSchema,
    PortalCollaborationListInvitationsRequestSchema,
    PortalCollaborationRespondInvitationRequestSchema,
    PortalCollaborationListMembersRequestSchema,
    PortalCollaborationInviteMemberRequestSchema,
    PortalCollaborationUpdateMemberRequestSchema,
    PortalCollaborationRemoveMemberRequestSchema,
    PortalCollaborationListResourcesRequestSchema,
    PortalCollaborationUpsertResourceRequestSchema,
    PortalCollaborationDeleteResourceRequestSchema,
    PortalCollaborationListAuditRequestSchema,
  ],
);

export const PortalCollaborationBrokerEventSchema = z
  .object({
    requestId: z.string().uuid(),
    request: PortalCollaborationRequestSchema,
  })
  .strict();

export const PortalCollaborationBrokerResponseSchema = z
  .object({
    status: z.number().int().min(100).max(599),
    body: z.unknown().nonoptional().nullable(),
  })
  .strict();

export type PortalCollaborationRequest = z.infer<
  typeof PortalCollaborationRequestSchema
>;
export type PortalCollaborationBrokerEvent = z.infer<
  typeof PortalCollaborationBrokerEventSchema
>;
export type PortalCollaborationBrokerResponse = z.infer<
  typeof PortalCollaborationBrokerResponseSchema
>;
